import { useCallback, useEffect, useState } from 'react';
import {
  AdminUserSummary,
  CreateAdminUserInput,
  CreateAdminUserResult,
  SetAdminUserStatusInput,
  UpdateAdminUserInput,
  adminAccessService,
} from '../services/firebaseFunctions';

const getErrorMessage = (error: unknown) => (
  error instanceof Error ? error.message : 'Não foi possível concluir a operação.'
);

/**
 * Hook de dados da página "Administradores" (REEDY-17).
 *
 * Segue o padrão Page → Hook → Service do projeto: nunca chama `httpsCallable` diretamente, e
 * nunca engole o objeto de erro original — os erros de `adminAccessService` (`AdminAccessError`,
 * com `.code`/`.details`) são relançados intactos para que a página distinga o fluxo de
 * confirmação em duas etapas (`failed-precondition` com `confirmReuseUid` pendente) de erros
 * terminais.
 */
export const useAdminUsers = () => {
  const [admins, setAdmins] = useState<AdminUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setAdmins(await adminAccessService.listAdminUsers());
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createAdmin = useCallback(async (input: CreateAdminUserInput): Promise<CreateAdminUserResult> => {
    try {
      const result = await adminAccessService.createAdminUser(input);
      await refresh();
      return result;
    } catch (caught) {
      // Propositalmente NÃO seta `error` aqui: o passo de confirmação de e-mail já existente é um
      // `failed-precondition` esperado que a página trata como diálogo, não como falha terminal.
      // A página decide o que exibir a partir do `caught`.
      throw caught;
    }
  }, [refresh]);

  const updateAdmin = useCallback(async (input: UpdateAdminUserInput) => {
    try {
      const result = await adminAccessService.updateAdminUser(input);
      await refresh();
      return result;
    } catch (caught) {
      setError(getErrorMessage(caught));
      throw caught;
    }
  }, [refresh]);

  const setAdminStatus = useCallback(async (input: SetAdminUserStatusInput) => {
    try {
      const result = await adminAccessService.setAdminUserStatus(input);
      await refresh();
      return result;
    } catch (caught) {
      throw caught;
    }
  }, [refresh]);

  const deleteAdmin = useCallback(async (uid: string) => {
    try {
      const result = await adminAccessService.deleteAdminUser({ uid });
      await refresh();
      return result;
    } catch (caught) {
      throw caught;
    }
  }, [refresh]);

  return {
    admins,
    loading,
    error,
    refresh,
    createAdmin,
    updateAdmin,
    setAdminStatus,
    deleteAdmin,
  };
};
